import React, { useEffect } from "react";
import {
  Box,
  Typography,
  Stack,
  Alert,
  CircularProgress,
  Grid,
} from "@mui/material";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { CloverField, FriendlyButton } from "../shared";

/**
 * CardPaymentForm Component
 * Handles Clover card tokenization
 * 
 * Note: Clover SDK initialization and element mounting is handled by parent
 * This component just renders the form and handles tokenization trigger
 */
const CardPaymentForm = ({
  t,
  cloverReady = false,
  cloverLoading = false,
  cloverError = "",
  cardTokenized = false,
  cardLast4 = "",
  cardBrand = "",
  onTokenize,
  onTokenized,
  tokenizeLoading = false,
  tokenizeError = null,
  fieldIdPrefix = "clover",
}) => {
  // Auto-call onTokenized when card is successfully tokenized
  useEffect(() => {
    if (cardTokenized && onTokenized) {
      // Small delay to show success message
      const timer = setTimeout(() => {
        onTokenized();
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [cardTokenized, onTokenized]);

  return (
    <Box sx={{ maxWidth: 600, mx: "auto" }}>
      {/* Header */}
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            backgroundColor: cardTokenized ? "success.100" : "primary.100",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 2,
          }}
        >
          {cardTokenized ? (
            <CheckCircleIcon sx={{ fontSize: 40, color: "success.main" }} />
          ) : (
            <CreditCardIcon sx={{ fontSize: 40, color: "primary.main" }} />
          )}
        </Box>
        <Typography
          variant="h5"
          sx={{ fontWeight: 700, color: cardTokenized ? "success.main" : "primary.main", mb: 1 }}
        >
          {cardTokenized 
            ? t("card_saved", "Card Saved!")
            : t("add_card", "Add Credit or Debit Card")
          }
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {cardTokenized
            ? t("card_ready", "Your card is ready to use")
            : t("enter_card_details", "Enter your card details securely")
          }
        </Typography>
      </Box>

      {/* Loading State */}
      {cloverLoading && (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 6 }}>
          <CircularProgress size={48} />
          <Typography sx={{ mt: 2, fontSize: "1.1rem" }}>
            {t("loading_card_form", "Loading secure card form...")}
          </Typography>
        </Box>
      )}

      {/* Error State */}
      {cloverError && (
        <Alert severity="error" sx={{ mb: 3, py: 2 }}>
          {cloverError}
        </Alert>
      )}

      {tokenizeError && (
        <Alert severity="error" sx={{ mb: 3, py: 2 }}>
          {tokenizeError}
        </Alert>
      )}

      {/* Card Tokenized Success */}
      {cardTokenized && (
        <Alert 
          icon={<CheckCircleIcon sx={{ fontSize: 32 }} />} 
          severity="success" 
          sx={{ py: 3, "& .MuiAlert-message": { fontSize: "1.2rem" } }}
        >
          {t("card_saved_success", `Your ${cardBrand || "card"} ending in ${cardLast4} has been saved`)}
        </Alert>
      )}

      {/* Card Form */}
      {cloverReady && !cardTokenized && (
        <Stack spacing={3}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <CloverField
                label={t("card_number", "Card Number")}
                fieldId={`${fieldIdPrefix}-card-number`}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <CloverField
                label={t("expiry_date", "Expiry Date")}
                fieldId={`${fieldIdPrefix}-card-date`}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <CloverField
                label={t("security_code", "Security Code (CVV)")}
                fieldId={`${fieldIdPrefix}-card-cvv`}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <CloverField
                label={t("postal_code", "Postal Code")}
                fieldId={`${fieldIdPrefix}-card-postal`}
              />
            </Grid>
          </Grid>

          <FriendlyButton
            onClick={onTokenize}
            disabled={tokenizeLoading}
            startIcon={tokenizeLoading ? <CircularProgress size={20} color="inherit" /> : <ArrowForwardIcon />}
          >
            {tokenizeLoading
              ? t("saving_card", "Saving Card...")
              : t("save_and_continue", "Save Card & Continue")
            }
          </FriendlyButton>
        </Stack>
      )}
    </Box>
  );
};

export default CardPaymentForm;
